import { tt } from '@agos/shared'

/**
 * The rights the order screens ask - `BACKEND.md` §10A.397, `FRONTEND.md` §9.744, owner O114, FB-1058.
 *
 * The owner: *"Práva roli / Upraviť obj tam chyba ... - práva na manuálnu zľavu / Práva na uplatnenie kupónu"*.
 * Editing an order, a manual discount and a coupon had no right of their own, and the server asks all three now -
 * so every control that leads to one asks it too, because a control offered and then refused is the defect §9.661
 * names.
 *
 * **Named here as well as on the dispatch board** (`manager-delivery`'s `dispatch-rules.ts`), because §5 forbids a
 * module importing another; both spell the catalogue's own key, which the roles screen's tests hold to the server.
 */
export const EDIT_ORDER = 'edit_order'
export const ORDER_DISCOUNT = 'order_discount'
export const APPLY_COUPON = 'apply_coupon'
/**
 * *Uplatnenie darčeka* - offering a gift in the cart, `BACKEND.md` §10A.416, `FRONTEND.md` §9.762, owner O124. It was
 * *Darčeky* (`gifts`), the right to manage the rules, which the cart's reads asked; `useCartGifts` asks this instead.
 */
export const APPLY_GIFT = 'apply_gift'
/** *Nová objednávka* - the route every *Upraviť* opens (`/new-order?edit=`), guarded by it - §9.744 amended. */
export const NEW_ORDER = 'new_order'

/**
 * Whether this person is offered *Upraviť* on an order - §9.744 amended, owner O114.
 *
 * **Both rights, each asked.** `edit_order` is what the server asks for the save (`BACKEND.md` §10A.397), and
 * `new_order` is what the editor's own route asks: every *Upraviť* opens *Nová objednávka*, so with `edit_order` alone
 * the button led to *Chýba právo* - a control offered and then refused, the defect §9.661 names. One rule for
 * *Predobjednávky*, *Reštaurácia* and the table map, so none of them can come to ask differently; the dispatch board
 * spells the same rule in `dispatch-rules.ts`, because §5 forbids a module importing another.
 */
export function mayOpenOrderEditor(hasPermission: (permission: string) => boolean): boolean {
  return hasPermission(EDIT_ORDER) && hasPermission(NEW_ORDER)
}

/** Which of the two boxes a screen draws - §9.744. */
export interface DiscountBoxes {
  /** The manual discount, and *Upraviť* on a discount already given - `order_discount`. */
  discount: boolean
  /** The coupon code - `apply_coupon`. */
  coupon: boolean
}

/**
 * The discount and coupon boxes this person is offered - §9.744.
 *
 * One rule for the order builder and *Zaplatiť*, asked of the session's own reader, so the two screens cannot come
 * to answer differently. **A box without its right is not drawn, and that is all this decides**: what the hidden box
 * leaves in the form is the caller's - and in the order builder's edit mode it must be the order's own figure, never
 * an emptied one (`seededDiscountOf`).
 */
export function discountBoxesFor(hasPermission: (permission: string) => boolean): DiscountBoxes {
  return { discount: hasPermission(ORDER_DISCOUNT), coupon: hasPermission(APPLY_COUPON) }
}

/**
 * Why the order builder will not open an order for editing, or `null` when it will - §9.744.
 *
 * **Only an edit asks**: taking a new order is `new_order`'s, which the route has already asked. Without the right
 * the screen says so rather than drawing an editor whose save the server refuses (§10A.397). `mayEditOrder` is
 * required - §9.480's rule, an optional flag is dropped at a call site with nothing to say so.
 *
 * The sentence names the **right**, under the roles screen's own label, as the dispatch board's does for *Vyraziť*
 * (§9.728): what the person needs is a tick in *Oprávnenia*, not a person to ask.
 */
export function editorRefusal(editId: number | null, mayEditOrder: boolean): string | null {
  if (editId === null || mayEditOrder) return null
  return `${tt('Na úpravu objednávky je potrebné právo')} „${tt('Upraviť objednávku')}“.`
}
